/**
 * Utilitaires robustes pour la géolocalisation avec fallbacks
 * Supporte navigateur et Capacitor
 */

// Import dynamique du SDK Capacitor GPS
let capacitorGpsSdk = null

async function loadCapacitorGps() {
  if (capacitorGpsSdk) return capacitorGpsSdk
  if (!window.isCapacitorApp) return null

  try {
    const module = await import('./capacitor-gps-sdk.js')
    capacitorGpsSdk = module
    return module
  } catch (e) {
    console.warn('Failed to load Capacitor GPS SDK:', e)
    return null
  }
}

/**
 * Détermine si on utilise Capacitor ou le navigateur
 */
function isCapacitorMode() {
  return window.isCapacitorApp === true
}

/**
 * Récupère la position via Capacitor GPS SDK
 * @returns {Promise<{lat:number, lng:number}>}
 */
async function getCapacitorLocation() {
  const sdk = await loadCapacitorGps()
  if (!sdk) {
    throw new Error('Capacitor GPS SDK non disponible')
  }

  const pos = await sdk.requestLocation()
  return {
    lat: pos.lat,
    lng: pos.lng,
  }
}

/**
 * Récupère la position avec timeout contrôlé (navigateur)
 * @param {object} options - Options de géolocalisation
 * @returns {Promise<{lat:number, lng:number}>}
 */
export function getLocationWithTimeout(options) {
  return new Promise((resolve, reject) => {
    const timeoutId = setTimeout(() => {
      reject(
        new Error(
          `Timeout expiré pour la géolocalisation (${options.timeout}ms)`,
        ),
      )
    }, options.timeout)

    if (!navigator.geolocation) {
      clearTimeout(timeoutId)
      reject(new Error('Géolocalisation non supportée par ce navigateur'))
      return
    }

    navigator.geolocation.getCurrentPosition(
      position => {
        clearTimeout(timeoutId)
        resolve({
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        })
      },
      error => {
        clearTimeout(timeoutId)
        reject(formatGeolocationError(error))
      },
      options,
    )
  })
}

/**
 * Récupère la position courante avec système de fallback à 3 niveaux
 * @param {object} options - Options de géolocalisation
 * @returns {Promise<{lat:number, lng:number}>}
 */
export async function getCurrentPositionWithFallback(options = {}) {
  // Si on est en mode Capacitor, utiliser le SDK natif
  if (isCapacitorMode()) {
    try {
      console.log('📱 Using Capacitor GPS SDK')
      return await getCapacitorLocation()
    } catch (capacitorError) {
      console.warn('Capacitor GPS failed, falling back to browser:', capacitorError)
      // Continue avec le fallback navigateur
    }
  }

  try {
    // Try with high accuracy first (short timeout)
    return await getLocationWithTimeout({
      enableHighAccuracy: true,
      timeout: 5000,
      maximumAge: 60000,
      ...options,
    })
  } catch (highAccuracyError) {
    console.warn(
      'High accuracy location failed, trying low accuracy:',
      highAccuracyError,
    )

    try {
      // Fallback to low accuracy
      return await getLocationWithTimeout({
        enableHighAccuracy: false,
        timeout: 10000,
        maximumAge: 300000,
        ...options,
      })
    } catch (lowAccuracyError) {
      console.warn(
        'Low accuracy location failed, using cached or default:',
        lowAccuracyError,
      )

      // Last resort: try with very loose settings
      return await getLocationWithTimeout({
        enableHighAccuracy: false,
        timeout: 3000,
        maximumAge: 3600000, // 1 hour cache
        ...options,
      })
    }
  }
}

/**
 * Formate les erreurs de géolocalisation
 * @param {GeolocationPositionError} error
 * @returns {Error}
 */
export function formatGeolocationError(error) {
  switch (error.code) {
    case error.PERMISSION_DENIED:
      return new Error('Permission de localisation refusée')
    case error.POSITION_UNAVAILABLE:
      return new Error('Position indisponible')
    case error.TIMEOUT:
      return new Error('Timeout de localisation')
    default:
      return new Error(`Erreur de localisation: ${error.message}`)
  }
}

/**
 * Gestionnaire d'erreurs de localisation avec messages contextualisés
 * @param {Error} error - Erreur de localisation
 * @param {Function} showNotification - Fonction pour afficher les notifications
 */
export function handleLocationError(error, showNotification) {
  let title = 'Erreur de localisation'
  let body = "Impossible d'obtenir votre position actuelle"
  let type = 'error'

  if (error.message.includes('Permission')) {
    title = 'Permission requise'
    body =
      "Veuillez autoriser l'accès à votre position dans les paramètres du navigateur"
    type = 'warning'
  } else if (error.message.includes('Timeout')) {
    title = "Délai d'attente dépassé"
    body =
      'La localisation prend trop de temps. Essayez de sélectionner manuellement votre position.'
    type = 'warning'
  } else if (error.message.includes('unavailable')) {
    title = 'Localisation indisponible'
    body =
      "Le service de localisation n'est pas disponible. Utilisez la recherche manuelle."
    type = 'warning'
  }

  if (showNotification) {
    showNotification({
      title,
      body,
      type,
      duration: 4000,
    })
  }

  return { title, body, type }
}

/**
 * Position par défaut (centre du Bénin)
 */
export const DEFAULT_LOCATION = {
  lat: 6.37,
  lng: 2.39,
}

// ==================== LIVE LOCATION TRACKING ====================

let activeWatchId = null
let capacitorLiveLocationActive = false

/**
 * Démarre le suivi de position en temps réel
 * @param {Function} onUpdate - Callback({lat, lng, accuracy}) appelé à chaque mise à jour
 * @param {object} options - Options de géolocalisation
 * @returns {Promise<number>} - Watch ID pour arrêter le suivi
 */
export async function startLiveLocation(onUpdate, options = {}) {
  if (!onUpdate || typeof onUpdate !== 'function') {
    throw new Error('Callback onUpdate requis pour le live location')
  }

  // Si on est en mode Capacitor, utiliser le SDK natif
  if (isCapacitorMode()) {
    try {
      console.log('📱 Starting Capacitor live location')
      const sdk = await loadCapacitorGps()
      if (sdk) {
        sdk.startLiveLocation(onUpdate)
        capacitorLiveLocationActive = true
        return 'capacitor'
      }
    } catch (e) {
      console.warn('Capacitor live location failed, using browser:', e)
    }
  }

  // Fallback navigateur
  if (!navigator.geolocation) {
    throw new Error('Géolocalisation non supportée')
  }

  activeWatchId = navigator.geolocation.watchPosition(
    position => {
      onUpdate({
        lat: position.coords.latitude,
        lng: position.coords.longitude,
        accuracy: position.coords.accuracy,
      })
    },
    error => {
      console.error('Live location error:', formatGeolocationError(error))
    },
    {
      enableHighAccuracy: true,
      timeout: 10000,
      maximumAge: 5000,
      ...options,
    },
  )

  return activeWatchId
}

/**
 * Arrête le suivi de position en temps réel
 * @param {number|string} watchId - Watch ID retourné par startLiveLocation
 */
export async function stopLiveLocation(watchId) {
  // Si c'était du Capacitor
  if (capacitorLiveLocationActive || watchId === 'capacitor') {
    const sdk = await loadCapacitorGps()
    if (sdk) {
      sdk.stopLiveLocation()
      capacitorLiveLocationActive = false
      return
    }
  }

  // Sinon, arrêter le watch du navigateur
  if (watchId && navigator.geolocation) {
    navigator.geolocation.clearWatch(watchId)
  }

  if (activeWatchId && navigator.geolocation) {
    navigator.geolocation.clearWatch(activeWatchId)
    activeWatchId = null
  }
}

/**
 * Wrapper pratique pour gérer automatiquement le nettoyage
 * @param {Function} onUpdate - Callback({lat, lng, accuracy})
 * @returns {Object} - { start, stop } pour contrôler le tracking
 */
export function createLiveLocationTracker(onUpdate) {
  let watchId = null

  return {
    /**
     * Démarre le tracking
     */
    async start(options = {}) {
      if (watchId !== null) {
        console.warn('Live location already started')
        return
      }
      watchId = await startLiveLocation(onUpdate, options)
    },

    /**
     * Arrête le tracking
     */
    async stop() {
      if (watchId !== null) {
        await stopLiveLocation(watchId)
        watchId = null
      }
    },

    /**
     * Vérifie si le tracking est actif
     */
    isActive() {
      return watchId !== null
    },
  }
}
